﻿import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Context, { context_type } from '@Util/Context';
import {
    str_isEmpty,
    write_console_error,
    generate_unique_id
} from '@Util/Utils';
import moment from 'moment';

import Popup from '@Elements/Popup/Popup';
import Datepicker_With_Label from '@Elements/FormElements/Datepicker_With_Label';
import Select_With_Label from '@Elements/FormElements/Select_With_Label';
import Submit_Button from '@Elements/FormElements/Submit_Button';
import Form_Error_Message from '@Elements/FormElements/Form_Error_Message';

import {
    Time_Log_Entry,
    Project,
    Edit_Schedule_Entry_Popup_Props,
    Customer
} from '@Util/Types';

export default function Edit_Schedule_Entry_Popup(props: Edit_Schedule_Entry_Popup_Props): JSX.Element {
    const [t] = useTranslation();
    const {
        weeks_time_log,
        customers,
        update_time_object,
        set_status_update
    }: context_type = useContext(Context);

    const [popup_open, set_popup_open] = useState<boolean>(false);
    const [selected_entry, set_selected_entry] = useState<Time_Log_Entry>();
    const [customer_projects, set_customer_projects] = useState<Project[]>([]);

    const [object_log_id, set_object_log_id] = useState<number>();
    const [object_customer_id, set_object_customer_id] = useState<number>();
    const [object_project_id, set_object_project_id] = useState<number>();
    const [object_start_date, set_object_start_date] = useState<string>();
    const [object_end_date, set_object_end_date] = useState<string>();
    const [object_start_date_error, set_object_start_date_error] = useState<boolean>(false);
    const [object_start_date_error_text, set_object_start_date_error_text] = useState<string>("");
    const [object_end_date_error, set_object_end_date_error] = useState<boolean>(false);
    const [object_end_date_error_text, set_object_end_date_error_text] = useState<string>("");
    const [form_is_processing, set_form_is_processing] = useState<boolean>(false);

    useEffect(() => {
        if (props?.entry_id?.length >= 1 && props?.edit_mode) {
            find_and_set_schedule_entry_object();
        }
    }, [props]);

    const customer_options: { value: number; text: string }[] = useMemo(() => {
        return customers
            ?.slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((customer: Customer) => ({
                value: customer.id,
                text: customer.name
            }));
    }, [customers]);

    const project_options: { value: number; text: string }[] = useMemo(() => {
        return customer_projects
            ?.filter(project => !project.isArchived)
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((project: Project) => ({
                value: project.id,
                text: project.name
            }));
    }, [object_customer_id, customer_projects]);

    async function find_and_set_schedule_entry_object(): Promise<void> {
        try {
            if (str_isEmpty(props.entry_id)) {
                throw new Error("Entry ID is empty.");
            }

            const entry_obj: Time_Log_Entry = await weeks_time_log?.find(m => m.id === parseInt(props?.entry_id));

            if (!entry_obj) {
                throw new Error("Entry object not found.");
            }

            const customer_obj: Customer = await customers?.find(m => m.id === entry_obj.customerId);

            set_selected_entry(entry_obj);
            set_customer_projects(customer_obj.projects.filter(m => m.isArchived === false));
            set_object_customer_id(entry_obj.customerId);
            set_object_project_id(entry_obj.projectId);
            set_object_log_id(entry_obj.id);
            set_object_start_date(entry_obj.startDate.toString());
            set_object_end_date(entry_obj.endDate.toString());
            set_popup_open(true);
        } catch (err) {
            write_console_error(err, err.message);
            set_status_update({
                type: "Error",
                text: err.message,
                visible: true
            });
        }
        
    }

    async function set_new_customer_projects(id: string): Promise<void> {
        try {
            const customer_obj: Customer = await customers?.find(m => m.id === parseInt(id));

            if (!customer_obj) {
                throw new Error("Customer object not found.");
            }

            set_object_customer_id(customer_obj.id);
            set_customer_projects(customer_obj.projects);
            set_object_project_id(customer_obj.projects[0].id);
        } catch (err) {
            write_console_error(err, err.message);
            set_status_update({
                type: "Error",
                text: err.message,
                visible: true
            });
        }
    }

    async function update_schedule_entry(e): Promise<void> {
        try {
            e.preventDefault();
            set_form_is_processing(true);

            // RESET DATE ERROR STATES
            set_object_start_date_error(false);
            set_object_start_date_error_text("");
            set_object_end_date_error(false);
            set_object_end_date_error_text("");

            let converted_start_date = moment(object_start_date);
            let converted_end_date = moment(object_end_date);

            if (converted_start_date > converted_end_date) {
                set_object_start_date_error(true);
                set_object_start_date_error_text(t('Text.StartDateMustComeBeforeEndDate'));
                set_form_is_processing(false);
                return;
            }

            if (converted_end_date < converted_start_date) {
                set_object_end_date_error(true);
                set_object_end_date_error_text(t('Text.EndDateMustComeAfterStartDate'));
                set_form_is_processing(false);
                return;
            }

            update_time_object(object_log_id, converted_start_date.toJSON(), converted_end_date.toJSON(), object_project_id);

            set_form_is_processing(false);
            props?.set_edit_mode(false);
            set_popup_open(false);
        } catch (err) {
            set_form_is_processing(false);
            write_console_error(err, err.message);
        }
    }

    function generate_error_message(): JSX.Element {
        if (object_end_date_error || object_start_date_error) {
            if (!str_isEmpty(object_start_date_error_text) || !str_isEmpty(object_end_date_error_text)) {
                let error_message: string = object_start_date_error_text ?? object_end_date_error_text;

                return <Form_Error_Message error={object_end_date_error || object_start_date_error} text={error_message} />
            }
        }

        return;
    }

    function close_edit_schedule_entry_popup(value: boolean): void {
        if (!value) {
            set_popup_open(false);
            props.set_edit_mode(false);
        } else {
            set_popup_open(true);
        }
    }

    return (
        <Popup
            open={popup_open}
            set_open={close_edit_schedule_entry_popup}
            title={`${t('Text.Edit')}: ${selected_entry?.customerName} - ${selected_entry?.projectName}`}
            disable_close_action={false}
        >
            <form onSubmit={update_schedule_entry}>
                {
                    !customer_options?.isEmpty() &&
                    <Select_With_Label
                        id={generate_unique_id()}
                        label={t('Label.Customer')}
                        value={object_customer_id}
                        onChange={(e) => {
                            set_object_customer_id(e.target.value);
                            set_new_customer_projects(e.target.value);
                        }}
                        options={customer_options}
                        error={false}
                    />
                }
                {
                    !project_options?.isEmpty() &&
                    <Select_With_Label
                        id={generate_unique_id()}
                        label={t('Label.Project')}
                        value={object_project_id}
                        onChange={(e) => set_object_project_id(e.target.value)}
                        options={project_options}
                        error={false}
                    />
                }
                {
                    !str_isEmpty(object_start_date) &&
                    <Datepicker_With_Label
                        id={generate_unique_id()}
                        label={t('Label.Start')}
                        selected={object_start_date}
                        onChange={value => set_object_start_date(value.toJSON())}
                        error={object_start_date_error}
                        maxDate={new Date()}
                    />
                }
                {
                    !str_isEmpty(object_end_date) &&
                    <Datepicker_With_Label
                        id={generate_unique_id()}
                        label={t('Label.End')}
                        selected={object_end_date}
                        onChange={value => set_object_end_date(value.toJSON())}
                        error={object_end_date_error}
                        maxDate={new Date()}
                    />
                }
                {generate_error_message()}
                <Submit_Button
                    text={t('Button.Save')}
                    disabled={form_is_processing}
                    loading={form_is_processing}
                />
            </form>
        </Popup>
    )
}